import React from 'react'
import { PiBuildings, PiChecks } from 'react-icons/pi'
import { cn } from '../UI/cn'
import { HelpTooltip } from '../Tooltip/HelpTooltip'
import type { ActionSummaryStructuredData } from '../../services/recommendations_api'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import type { RecommendationAgent } from '../../models/companies'
import {
  URGENCY_ICONS,
  URGENCY_TOOLTIPS,
  URGENCY_BADGE_CLASSES,
} from './constants'
import { resolveAgentIcon } from './agentIcons'

type ViewMode = 'priority' | 'signal' | 'company'

interface SummaryStatsHeaderProps {
  data: ActionSummaryStructuredData
  className?: string
  visibleActions?: EnrichedAction[]
  viewMode?: ViewMode
  /** Number of companies with active actions (used in company view). */
  companiesCount?: number
  /**
   * User-facing agents from the backend AGENT_REGISTRY (delivered via
   * company-details). Drives the signal-view chips so adding a new agent
   * server-side automatically adds a chip — no FE update needed.
   */
  recommendationAgents?: RecommendationAgent[]
}

interface StatChipProps {
  /** Icon shown before the count. */
  indicator: React.ReactNode
  value: number | string
  label: string
  tooltipContent: string
}

const StatChip: React.FC<StatChipProps> = ({
  indicator,
  value,
  label,
  tooltipContent,
}) => (
  <div className="flex items-center gap-2">
    {indicator}
    <span className="text-base font-bold leading-none tabular-nums text-fg">
      {value}
    </span>
    <span className="text-base font-normal leading-none text-fg-secondary">
      {label}
    </span>
    <HelpTooltip content={tooltipContent} placement="top" />
  </div>
)

/**
 * Summary stats header with stat cards whose content adapts to the active view mode.
 *
 * - Priority view: Do Now / Today / This Week counts (urgency bands)
 * - Signal/Type view: One chip per enabled agent — agents come from the
 *   backend AGENT_REGISTRY via company-details, so adding a new agent
 *   server-side automatically adds its chip with no FE change.
 * - Company view: Companies / Active Actions counts
 */
export const SummaryStatsHeader: React.FC<SummaryStatsHeaderProps> = ({
  data,
  className,
  visibleActions,
  viewMode = 'priority',
  companiesCount = 0,
  recommendationAgents = [],
}) => {
  const actions = visibleActions ?? data.enriched_actions ?? []

  // Urgency band counts
  const doNowCount = actions.filter((a) => a.urgency === 'do_now').length
  const todayCount = actions.filter((a) => a.urgency === 'today').length
  const thisWeekCount = actions.filter((a) => a.urgency === 'this_week').length

  const totalActive = actions.length

  const DoNowIcon = URGENCY_ICONS.do_now
  const TodayIcon = URGENCY_ICONS.today
  const ThisWeekIcon = URGENCY_ICONS.this_week

  const agentChips: StatChipProps[] = recommendationAgents
    .filter((agent) => agent.enabled)
    .map((agent) => {
      const Icon = resolveAgentIcon(agent.icon)
      const memberSet = new Set(agent.signal_types)
      const count = actions.filter((a) => memberSet.has(a.signal_type)).length
      return {
        indicator: (
          <span className="size-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-brand-active/10 text-brand-active">
            <Icon size={14} />
          </span>
        ),
        value: count,
        label: agent.label,
        tooltipContent: agent.description,
      }
    })

  const chips: StatChipProps[] =
    viewMode === 'priority'
      ? [
          {
            indicator: (
              <span
                className={cn(
                  'size-7 rounded-lg flex items-center justify-center flex-shrink-0',
                  URGENCY_BADGE_CLASSES.do_now
                )}
              >
                <DoNowIcon size={14} />
              </span>
            ),
            value: doNowCount,
            label: 'Hot',
            tooltipContent: URGENCY_TOOLTIPS.do_now.content,
          },
          {
            indicator: (
              <span
                className={cn(
                  'size-7 rounded-lg flex items-center justify-center flex-shrink-0',
                  URGENCY_BADGE_CLASSES.today
                )}
              >
                <TodayIcon size={14} />
              </span>
            ),
            value: todayCount,
            label: 'Today',
            tooltipContent: URGENCY_TOOLTIPS.today.content,
          },
          {
            indicator: (
              <span
                className={cn(
                  'size-7 rounded-lg flex items-center justify-center flex-shrink-0',
                  URGENCY_BADGE_CLASSES.this_week
                )}
              >
                <ThisWeekIcon size={14} />
              </span>
            ),
            value: thisWeekCount,
            label: 'This Week',
            tooltipContent: URGENCY_TOOLTIPS.this_week.content,
          },
        ]
      : viewMode === 'signal'
        ? agentChips
        : [
            {
              indicator: (
                <span className="size-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-brand-active/10 text-brand-active">
                  <PiBuildings size={14} />
                </span>
              ),
              value: companiesCount,
              label: 'Companies',
              tooltipContent:
                'Number of companies with at least one active recommended action.',
            },
            {
              indicator: (
                <span className="size-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-brand-active/10 text-brand-active">
                  <PiChecks size={14} />
                </span>
              ),
              value: totalActive,
              label: 'Total',
              tooltipContent: `${totalActive} active action${totalActive === 1 ? '' : 's'} across ${companiesCount} ${companiesCount === 1 ? 'company' : 'companies'}.`,
            },
          ]

  return (
    <div className={cn(className)}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {chips.map((chip, i) => (
          <React.Fragment key={chip.label}>
            <StatChip {...chip} />
            {i < chips.length - 1 && (
              <span className="w-px h-6 bg-line flex-shrink-0 self-center" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
